import React, { useEffect, useRef, useState } from 'react';
import { isLockedSkill } from './utils';

const LONG_PRESS_MS = 500;

// Transform abilities (skillset 8) are grouped by their tier within their tab.
const TRANSFORM_SKILLSET_ID = 8;
const TIER_LABELS = ['Static', 'Basic', 'Advanced', 'Expert', 'Master', 'Grandmaster', 'Mythic'];

// Group skills by tier and return entries sorted by tier (ascending).
const groupByTier = (skills) => {
    const byTier = {};
    skills.forEach((s) => {
        const t = s.tier ?? 0;
        (byTier[t] = byTier[t] || []).push(s);
    });
    return Object.entries(byTier).sort(([a], [b]) => Number(a) - Number(b));
};

// Base/increment SP rate for a not-yet-purchased skill, picking the primary/
// secondary/tertiary tier the same way computeSkillCosts does for a
// single-purchase skill (CharacterSkills.js).
const skillRate = (skill, character) => {
    if (skill.secondaryBaseCost == null) {
        return { base: skill.primaryBaseCost, increment: skill.primaryIncrementCost };
    }
    if (Number(skill.skillset) === Number(character?.skillsetPrimary)) {
        return { base: skill.primaryBaseCost, increment: skill.primaryIncrementCost };
    }
    if (Number(skill.skillset) === Number(character?.skillsetSecondary)) {
        return { base: skill.secondaryBaseCost, increment: skill.secondaryIncrementCost };
    }
    return { base: skill.tertiaryBaseCost, increment: skill.tertiaryIncrementCost };
};

// Left click/tap adds the skill; right click or long-press toggles its
// description (rendered separately, full-width, by AvailableSkills).
const PurchasableSkillCard = ({ skill, disabled, dimmed, active, onSingleClick, onToggleDescription, cost, rate }) => {
    const longPressTimeout = useRef();
    const longPressFired = useRef(false);

    // Right-click (desktop) → toggle description
    const handleContextMenu = (e) => {
        e.preventDefault();
        if (onToggleDescription) onToggleDescription(skill.skillId);
    };

    // Touch long-press → toggle description (and suppress the follow-up click)
    const handleTouchStart = () => {
        longPressFired.current = false;
        longPressTimeout.current = setTimeout(() => {
            longPressFired.current = true;
            if (onToggleDescription) onToggleDescription(skill.skillId);
        }, LONG_PRESS_MS);
    };
    const clearLongPress = () => clearTimeout(longPressTimeout.current);

    const handleClick = (e) => {
        if (longPressFired.current) {
            // This click is the tail end of a long-press — don't also add the skill.
            longPressFired.current = false;
            e.preventDefault();
            return;
        }
        if (!disabled && onSingleClick) onSingleClick(skill);
    };

    return (
        <div
            className={
                'draggable-skill' +
                (disabled ? ' disabled-skill' : (dimmed ? ' dimmed-skill' : '')) +
                (active ? ' desc-active' : '')
            }
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-disabled={disabled}
            title={
                disabled
                    ? "Skill already added"
                    : dimmed
                        ? `Add another rank of ${skill.name}`
                        : `Add ${skill.name}`
            }
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            onTouchStart={handleTouchStart}
            onTouchEnd={clearLongPress}
            onTouchMove={clearLongPress}
            onTouchCancel={clearLongPress}
        >
            {skill.name} <span className="skill-rate-inline">[{rate.base}+{rate.increment}]</span> <span className="skill-cost-inline">({cost} SP)</span>
        </div>
    );
};




const AvailableSkills = ({
                             availableSkills,
                             skillsetMap,
                             activeSkillsetTab,
                             setActiveSkillsetTab,
                             currentCharacter,
                             getSkillCost,
                             getActionCost,
                             handleDropSkill,
                             onIncrement
                         }) => {
    // Which skill's description is unfolded (full-width, one at a time).
    const [expandedId, setExpandedId] = useState(null);
    const toggleDescription = (id) => setExpandedId(prev => (prev === id ? null : id));
    // Collapse the open description when the active tab changes.
    useEffect(() => { setExpandedId(null); }, [activeSkillsetTab]);

    // Group the skills by skillset. Locked skills (the always-granted innate
    // ones and auto-added transform abilities) are excluded — they can't be
    // bought or removed. Removable granted skills (e.g. Spell Strike) stay.
    const grouped = availableSkills
        .filter(skill => !isLockedSkill(skill))
        .reduce((groups, skill) => {
            const groupKey = String(skill.skillset);
            if (!groups[groupKey]) groups[groupKey] = [];
            groups[groupKey].push(skill);
            return groups;
        }, {});

    // Sort skills within each group by name
    Object.values(grouped).forEach(skills =>
        skills.sort((a, b) => a.name.localeCompare(b.name))
    );

    // Sort tabs by skillset name
    const groupKeys = Object.keys(grouped).sort((a, b) => {
        const nameA = (skillsetMap[a] || "").toUpperCase();
        const nameB = (skillsetMap[b] || "").toUpperCase();
        return nameA.localeCompare(nameB);
    });

    // Render one purchasable skill card (plus its unfolded description, if open).
    const renderSkill = (skill) => {
        const owned = currentCharacter.skills?.find(s => s.skillId === skill.skillId);
        // A skill can be bought again if any tier has a positive increment cost.
        const isMultiPurchase = [
            skill.primaryIncrementCost,
            skill.secondaryIncrementCost,
            skill.tertiaryIncrementCost
        ].some(v => Number(v) > 0);

        // Owned skills grey out; single-purchase owned skills also stop being clickable.
        const isDisabled = !!owned && !isMultiPurchase;
        const isDimmed = !!owned;

        // SP cost of the *next* rank this click would buy.
        const nextRanks = owned ? Number(owned.ranks) + 1 : 1;
        const effectiveSkill = owned ? { ...skill, ranks: Number(owned.ranks) } : skill;
        const computedCost = getActionCost
            ? getActionCost(effectiveSkill, nextRanks)
            : getSkillCost(skill, 1);

        const onClickSkill = () => {
            if (owned) {
                if (isMultiPurchase && onIncrement) onIncrement(skill.skillId);
            } else {
                handleDropSkill(skill);
            }
        };

        const card = (
            <PurchasableSkillCard
                key={skill.skillId}
                skill={skill}
                cost={computedCost}
                rate={skillRate(skill, currentCharacter)}
                disabled={isDisabled}
                dimmed={isDimmed}
                active={expandedId === skill.skillId}
                onSingleClick={onClickSkill}
                onToggleDescription={toggleDescription}
            />
        );

        if (expandedId !== skill.skillId) return [card];

        // Full-width description that unfolds under the tapped skill.
        return [
            card,
            <div
                key={`desc-${skill.skillId}`}
                className="available-desc-row"
                dangerouslySetInnerHTML={{
                    __html: skill.description || 'No description available.'
                }}
            />
        ];
    };

    return (
        <div className="available-skills-tabbed">
            <div className="tabs-header">
                {groupKeys.map(key => (
                    <button
                        key={key}
                        className={`tab-button${activeSkillsetTab === key ? ' active' : ''}`}
                        onClick={() => setActiveSkillsetTab(key)}
                        type="button"
                    >
                        {skillsetMap[key] || `Skillset ${key}`}
                    </button>
                ))}
            </div>
            <div className="tab-content">
                {!(activeSkillsetTab && grouped[activeSkillsetTab]) ? (
                    <p>No skills available in this category.</p>
                ) : Number(activeSkillsetTab) === TRANSFORM_SKILLSET_ID ? (
                    // Transform tab: group abilities under a per-tier header.
                    groupByTier(grouped[activeSkillsetTab]).map(([tier, skills]) => (
                        <React.Fragment key={`tier-${tier}`}>
                            <div className="transform-tier-header">
                                {TIER_LABELS[Number(tier)] || `Tier ${tier}`}
                            </div>
                            {skills.flatMap(renderSkill)}
                        </React.Fragment>
                    ))
                ) : (
                    grouped[activeSkillsetTab].flatMap(renderSkill)
                )}
            </div>
        </div>
    );
};

export default AvailableSkills;
